import React, { useEffect, useMemo, useState } from "react";
import "./clasificaciones.css";

function Clasificaciones() {
    const [competiciones, setCompeticiones] = useState([]);
    const [seleccion, setSeleccion] = useState(null);
    const [abiertoComp, setAbiertoComp] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState("");
    const [partidos, setPartidos] = useState([]);
    const [codeMap, setCodeMap] = useState(new Map());

    const displayNombre = (raw) => {
        if (!raw) return "";
        const pretty = raw.replace(/_/g, " ").toLowerCase();
        return pretty.charAt(0).toUpperCase() + pretty.slice(1);
    };
    const safe = (obj, key) => (obj?.[key] ?? obj?.[key?.toLowerCase?.()] ?? "");

    const resolveClubId = (externalIdString) => {
        const key = (externalIdString ?? "").toString().trim();
        if (!key) return null;
        const byCode = codeMap.get(key);
        if (byCode) return byCode;
        if (/^[0-9a-fA-F]{24}$/.test(key)) return key;
        return null;
    };

    useEffect(() => {
        fetch("/api/resultados/competiciones")
            .then((r) => (r.ok ? r.json() : []))
            .then(setCompeticiones)
            .catch(() => setCompeticiones([]));

        fetch("/api/clubs/codes-map")
            .then((r) => (r.ok ? r.json() : {}))
            .then((obj) => {
                const m = new Map();
                for (const [k, v] of Object.entries(obj || {})) {
                    if (k && v) m.set(String(k).trim(), String(v));
                }
                setCodeMap(m);
            })
            .catch(() => setCodeMap(new Map()));
    }, []);

    const cargarCompeticion = async (name) => {
        setSeleccion(name);
        setAbiertoComp(false);
        setCargando(true);
        setError("");
        try {
            const r = await fetch(`/api/resultados?coleccion=${encodeURIComponent(name)}`);
            if (!r.ok) throw new Error("No se pudieron cargar los resultados");
            const data = await r.json();
            setPartidos(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.message || "Error cargando datos");
            setPartidos([]);
        } finally {
            setCargando(false);
        }
    };

    /* ===== Cálculo de clasificación ===== */
    const tabla = useMemo(() => {
        const hasScore = (v) =>
            v !== null &&
            v !== undefined &&
            !(typeof v === "string" && v.trim() === "") &&
            !Number.isNaN(Number(v));
        const toNum = (v) => Number(v);
        const acc = new Map();

        const addTeam = (name, idExt) => {
            if (!name) return;
            if (!acc.has(name)) {
                acc.set(name, {
                    pj: 0, w: 0, d: 0, l: 0, gf: 0, ga: 0, gd: 0, pts: 0,
                    clubId: resolveClubId(idExt)
                });
            }
        };

        for (const m of partidos) {
            const local = safe(m, "EQUIPO_LOCAL");
            const visit = safe(m, "EQUIPO_VISITANTE");
            const gl = m["GOLES_LOCAL"];
            const gv = m["GOLES_VISITANTE"];

            const idLocalExt = (m.id_local ?? m.ID_LOCAL ?? m.Id_Local ?? "").toString();
            const idVisitExt = (m.id_visitante ?? m.ID_VISITANTE ?? m.Id_Visitante ?? "").toString();

            addTeam(local, idLocalExt);
            addTeam(visit, idVisitExt);

            if (!(hasScore(gl) && hasScore(gv))) continue;

            const nGl = toNum(gl);
            const nGv = toNum(gv);

            const a = acc.get(local);
            const b = acc.get(visit);

            a.pj += 1; b.pj += 1;
            a.gf += nGl; a.ga += nGv; a.gd = a.gf - a.ga;
            b.gf += nGv; b.ga += nGl; b.gd = b.gf - b.ga;

            if (nGl > nGv) {
                a.w += 1; a.pts += 2;
                b.l += 1;
            } else if (nGl < nGv) {
                b.w += 1; b.pts += 2;
                a.l += 1;
            } else {
                a.d += 1; a.pts += 1;
                b.d += 1; b.pts += 1;
            }
        }

        const filas = Array.from(acc.entries()).map(([team, s]) => ({
            equipo: team,
            escudoId: s.clubId,
            ...s
        }));

        filas.sort((x, y) => {
            if (y.pts !== x.pts) return y.pts - x.pts;
            if (y.gd !== x.gd) return y.gd - x.gd;
            if (y.gf !== x.gf) return y.gf - x.gf;
            return x.equipo.localeCompare(y.equipo, "es", { sensitivity: "base" });
        });

        return filas.map((f, i) => ({ pos: i + 1, ...f }));
    }, [partidos, codeMap]);

    return (
        <div className="cls-wrap">
            <div className="cls-header">
                <h1 className="title">Clasificaciones</h1>
                <div className="cls-right">
                    <div className="dropdown">
                        <button className="btn-create" onClick={() => setAbiertoComp((v) => !v)}>
                            {seleccion
                                ? `Competición: ${displayNombre(seleccion)}`
                                : "Seleccionar competición"}
                        </button>
                        {abiertoComp && (
                            <div className="dropdown-panel" role="menu">
                                {competiciones.length === 0 && (
                                    <div className="dropdown-empty">Cargando competiciones</div>
                                )}
                                {[...competiciones]
                                    .sort((a, b) =>
                                        a.localeCompare(b, "es", { sensitivity: "base" })
                                    )
                                    .map((name) => (
                                        <button
                                            key={name}
                                            className="dropdown-item"
                                            onClick={() => cargarCompeticion(name)}
                                        >
                                            {displayNombre(name)}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {error && <div className="cls-error">{error}</div>}
            {cargando && <div className="cls-loading">Cargando…</div>}

            {!cargando && !error && seleccion && (
                <div className="cls-card">
                    <div className="cls-table">
                        <div className="cls-thead">
                            <div className="th pos">#</div>
                            <div className="th equipo">Equipo</div>
                            <div className="th pts">Pts</div>
                            <div className="th pj">PJ</div>
                            <div className="th w">W</div>
                            <div className="th d">D</div>
                            <div className="th l">L</div>
                            <div className="th gd">G.AVG</div>
                            <div className="th gf">GF</div>
                            <div className="th ga">GC</div>
                        </div>

                        <div className="cls-tbody">
                            {tabla.map((r) => (
                                <div className="tr" key={r.equipo}>
                                    <div className="td pos">{r.pos}</div>
                                    <div className="td equipo">
                                        <span className="team-cell">
                                            {r.escudoId ? (
                                                <img
                                                    className="escudo-mini"
                                                    src={`/api/clubs/${r.escudoId}/escudo`}
                                                    alt={`Escudo ${r.equipo}`}
                                                    onError={(e) =>
                                                        (e.currentTarget.style.display = "none")
                                                    }
                                                />
                                            ) : null}
                                            <span className="team-name">{r.equipo}</span>
                                        </span>
                                    </div>
                                    <div className="td pts">{r.pts}</div>
                                    <div className="td pj">{r.pj}</div>
                                    <div className="td w">{r.w}</div>
                                    <div className="td d">{r.d}</div>
                                    <div className="td l">{r.l}</div>
                                    <div className="td gd">{r.gd}</div>
                                    <div className="td gf">{r.gf}</div>
                                    <div className="td ga">{r.ga}</div>
                                </div>
                            ))}

                            {tabla.length === 0 && (
                                <div className="cls-empty">
                                    No hay datos para esta competición.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!seleccion && !cargando && (
                <div className="cls-empty">
                    Selecciona una competición para ver su clasificación.
                </div>
            )}
        </div>
    );
}

export default Clasificaciones;
